import { CSSProperties, useState } from 'react';
import { ReactComponent as AddArtistIcon } from 'assets/images/add-artist-profile.svg';
import { ReactComponent as InstagramLogo } from 'assets/images/instagram-bordered-icon.svg';
import { ReactComponent as YoutubeLogo } from 'assets/images/streaming/icons/youtube-logo.svg';
import { ReactComponent as TiktokLogo } from 'assets/images/tiktok-black.svg';
import { useHistory } from 'react-router-dom';
import { SpotifyArtistSearch as SpotifyArtistSearchType } from './interfaces';
import { RowProfileItem } from './Components/RowProfileItem';
import { CreateBrandPayload } from 'api/brandApi';
import ModalSymphony from 'elements/ModalSymphony';
import SpotifyArtistSearch from 'components/shareable/SpotifyArtistSearch';
import { CurrentBrand, SpotifyArtistSearchResponse } from 'modules/Types';
import Colors from 'modules/Colors';
import { clsx } from 'modules/Utils';
import SocialConnectionsButtonsWithModals from 'components/shareable/SocialConnectionsButtonsWithModals';
import useCurrentBrand from 'Hooks/useCurrentBrand';
import useOrganization from 'Hooks/useOrganization';

/** Props for the AddBrandToOrganizationModal component */
interface AddBrandToOrganizationModalProps {
  /** Optional class name for styling */
  className?: string;
  /** Controls modal visibility */
  isOpen: boolean;
  /** Handler for closing the modal */
  onClose: () => void;
  /** Callback fired when artist is successfully added to organization */
  onFinishAddingToOrganization: () => void;
}

const AddBrandToOrganizationModal = ({
  className,
  isOpen,
  onClose,
  onFinishAddingToOrganization
}: AddBrandToOrganizationModalProps) => {
  const { createBrand, setCurrentBrand } = useCurrentBrand();
  const { organization, setOrganization, organizationBrandIds, setOrganizationBrandIds } = useOrganization();

  const [error, setError] = useState<string | null>(null);
  const [spotifyResults, setSpotifyResults] = useState<SpotifyArtistSearchType[]>([]);
  const history = useHistory();

  const handleSelectArtist = async (
    artist: SpotifyArtistSearchResponse | null,
    existingBrand?: CurrentBrand | null
  ) => {
    if (!artist) {
      return
    }

    try {
      if (!existingBrand) {
        const brandPayload: CreateBrandPayload = {
          name: artist.name,
          customization: {
            spotify_artist: artist
          }
        }
        const newBrand = await createBrand(brandPayload)
        const organizationLocal = { ...organization!, brands: [...organization!.brands, newBrand] };
        setOrganization(organizationLocal);
        setOrganizationBrandIds([...organizationBrandIds, newBrand.id])
        setCurrentBrand(newBrand)
        onFinishAddingToOrganization();
        onClose();
      }
    } catch (error) {
      console.log("handleSelectArtist::error", error)
    }
  };

  const renderSocialMediaNoResults = () => {
    return (
      <div
        className="anotherSocialPlatformContainer"
        style={sty.anotherSocialPlatformContainer}
      >
        <div
          className="createAccountWithoutSpotifyText2"
          style={sty.createAccountWithoutSpotifyText2}
        >
          Or add it linking the social platform profile:
        </div>
        <div
          className="platformsButtonsContainer"
          style={sty.platformsButtonsContainer}
        >
          <div
            className="platformButtonTikTokContainer"
            style={sty.platformButtonTikTokContainer}
          >
            <TiktokLogo width={32} height={32} />
          </div>
          <div
            className="platformButtonInstagramContainer"
            style={sty.platformButtonInstagramContainer}
          >
            <InstagramLogo width={32} height={32} />
          </div>
          <div
            className="platformButtonYoutubeContainer"
            style={sty.platformButtonYoutubeContainer}
          >
            <YoutubeLogo width={32} height={32} />
          </div>
        </div>
      </div>
    )
  }

  const renderSpotifySearchResults = () => {
    return (
      <ul className="addBrandToOrganizationModalSpotifySearchResults add-new mb-5 max-h-80 overflow-auto">
        {spotifyResults.map((artist, index) => {
          switch (artist?.status) {
            case 'new_profile':
              return (
                <RowProfileItem
                  key={`artist_${artist.id}_${index}`}
                  className="newProfileArtistItem"
                  artist={artist}
                  actionLabel="Connect"
                  handleArtist={handleSelectArtist}
                />
              );
            case 'existing_profile':
              return (
                <RowProfileItem
                  key={`artist_${artist.id}_${index}`}
                  className="existingProfileArtistItem"
                  artist={artist}
                  actionLabel="Add to Team"
                  handleArtist={(artist) => handleSelectArtist(artist, artist)}
                  footerLabel={`You already have access to this profile, do you want to add ${artist.name} to your Team account?`}
                />
              );
            default:
              return (
                <RowProfileItem
                  key={`artist_${artist.id}_${index}`}
                  className="teamProfileArtistItem"
                  artist={artist}
                  actionLabel="Already in Team"
                  handleArtist={() => { }}
                  footerLabel={`You already have access to ${artist.name} in your Team account`}
                />
              );
          }
        })}
      </ul>
    )
  }

  return (
    <ModalSymphony
      className={clsx('addNewArtistModalComponent', className)}
      isOpen={isOpen}
      onClose={onClose}
      title="Add a Profile"
      subtitle="Setup a new profile to add to your Team."
      headerIcon={<AddArtistIcon />}
    >
      <SpotifyArtistSearch
        className="spotifyArtistSearch"
        title="Enter your artist name or Spotify URL"
        onSelectArtist={handleSelectArtist}
        onClickCreateNewProfile={() => history.push('/add-artist')}
      />

      {/* {spotifyResults.length <= 0 ?
        renderSocialMediaNoResults()
        :
        renderSpotifySearchResults()
      } */}

      {renderSpotifySearchResults()}
      {/* {renderSocialMediaNoResults()} */}
      <SocialConnectionsButtonsWithModals
        selectedTikTokProfile={null}
        selectedInstagramProfile={null}
        onTikTokSelect={() => { }}
        onInstagramSelect={() => { }}
        onSignupWithoutConnections={() => { }}
      />
    </ModalSymphony>
  );
};

const sty: Record<string, CSSProperties> = {
  anotherSocialPlatformContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    width: '100%',
    gap: 8
  },
  createAccountWithoutSpotifyText2: {
    fontSize: 18,
    color: '#707070',
    marginTop: 32
  },
  platformsButtonsContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    gap: 16
  },
  platformButtonYoutubeContainer: {
    display: 'grid',
    placeItems: 'center',
    width: 48,
    height: 48,
    borderRadius: 12,
    cursor: 'pointer',
    background: Colors.BACKGROUND_YOUTUBE_ICON
  },
  platformButtonTikTokContainer: {
    display: 'grid',
    placeItems: 'center',
    width: 48,
    height: 48,
    borderRadius: 12,
    cursor: 'pointer',
    background: Colors.BACKGROUND_TIKTOK_ICON
  },
  platformButtonInstagramContainer: {
    display: 'grid',
    placeItems: 'center',
    width: 48,
    height: 48,
    borderRadius: 12,
    cursor: 'pointer',
    background: Colors.GRADIENT_INSTAGRAM
  },
};

export default AddBrandToOrganizationModal;